<template>
  <div class="editor-layout">
    <CategoryScrollPanel
      v-if="rootCategories.length"
      class="cat-col"
      :categories="rootCategories"
      :active-category-id="activeCategoryId"
      :horizontal="isMobile"
      @select="onRootCategorySelect"
    />

    <section class="editor-preview">
      <div class="preview-toolbar">
        <button type="button" class="back-overview" @click="goBackToOverview">← Übersicht</button>
      </div>

      <CategoryScrollPanel
        v-if="subCategories.length"
        class="subcat-bar"
        :categories="subCategories"
        :active-category-id="categoryId"
        :horizontal="true"
        @select="onSubCategorySelect"
      />

      <div class="preview-wrap">
        <ConfiguratorPreview
          :aspect-ratio="aspectRatio"
          :active-tab="activeTab"
          :model-config="resolvedPreviewConfig"
          :preview3d="preview3dLayers"
          :show-configure-cta="isMobile && !mobileSidebarOpen"
          @close-group="setActiveTab(null)"
          @configure="mobileSidebarOpen = true"
        />
      </div>
    </section>

    <aside v-if="!isMobile" class="editor-sidebar">
      <ConfiguratorSidebar
        :logo-url="logoUrl || undefined"
        :product-id="productId || undefined"
        :category="categoryId || undefined"
        :show-upload="true"
        :is-multi-mode="false"
        @change-size="updateCanvasSize"
        @change-group="setActiveTab"
      />
    </aside>

    <template v-else>
      <div
        v-if="mobileSidebarOpen"
        class="mobile-backdrop"
        @click="mobileSidebarOpen = false"
      />
      <aside
        class="mobile-sidebar"
        :class="{ open: mobileSidebarOpen }"
        :aria-hidden="!mobileSidebarOpen"
      >
        <div class="mobile-sidebar-head">
          <button type="button" class="mobile-close" @click="mobileSidebarOpen = false">
            Schließen
          </button>
        </div>
        <ConfiguratorSidebar
          :logo-url="logoUrl || undefined"
          :product-id="productId || undefined"
          :category="categoryId || undefined"
          :show-upload="true"
          :is-multi-mode="false"
          @change-size="updateCanvasSize"
          @change-group="setActiveTab"
        />
      </aside>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery } from '@vue/apollo-composable'

import ConfiguratorPreview from '../editor/configurator/preview.vue'
import ConfiguratorSidebar from '../editor/configurator/sidebar.vue'
import CategoryScrollPanel from '../editor/CategoryScrollPanel.vue'
import { getCategory, getProduct } from '../../gql/product/query'
import { fetchEditorCategoryTree, type EditorCategoryTreeResponse } from '../../services/editorCategoryApi'
import { resolvePreview3dOverrides } from '../../utils/editorConfigResolver'
import { useRouteQueryIds } from '../../composables/useRouteQueryIds'
import { useImageDB, type ImageRecord } from '../../utils/imageDb'
import { useProjectStore } from '../../store/useProjectStore'
import { getWidgetConfig } from '../../runtime/widgetConfig'

const route = useRoute()
const router = useRouter()
const db = useImageDB('imageDB')
const project = useProjectStore()
const EDITOR_IMAGES_CHANGED_EVENT = 'pfe:editor-images-changed'
const logoUrl = (() => { try { return getWidgetConfig().logoUrl || '' } catch { return '' } })()

const aspectRatio = ref(75 / 30)
const activeTab = ref<string | null>(null)
const isMobile = ref(false)
const mobileSidebarOpen = ref(false)
const projectCategoryCounts = ref<Map<number, number>>(new Map())
const projectRootCategoryCounts = ref<Map<number, number>>(new Map())
const categoryTree = ref<EditorCategoryTreeResponse | null>(null)

async function loadCategoryTree() {
  categoryTree.value = await fetchEditorCategoryTree({
    editorBaseUrl: getWidgetConfig().editorBaseUrl,
  })
}

async function refreshProjectImageCounts() {
  project.loadFromStorage()
  const sessionCode = project.projectCode.value
  let records: ImageRecord[] = []
  if (sessionCode) {
    const byProject = await db.getByProjectCode(sessionCode)
    const seen = new Set(byProject.map((r) => r.id))
    const allRecords = await db.getAll()
    const orphans = allRecords.filter((r: any) => !(r as any)?.projectCode && !seen.has(r.id))
    records = [...byProject, ...orphans]
  } else {
    records = await db.getAll()
  }

  const byCategory = new Map<number, number>()
  const byRoot = new Map<number, number>()
  for (const rec of records) {
    const categoryId = Number((rec as any)?.categoryId ?? 0)
    const rootCategoryId = Number((rec as any)?.parentCategoryId ?? categoryId ?? 0)
    if (categoryId > 0) byCategory.set(categoryId, (byCategory.get(categoryId) ?? 0) + 1)
    if (rootCategoryId > 0) byRoot.set(rootCategoryId, (byRoot.get(rootCategoryId) ?? 0) + 1)
  }
  projectCategoryCounts.value = byCategory
  projectRootCategoryCounts.value = byRoot
}

function onExternalEditorImagesChanged() {
  refreshProjectImageCounts().catch(() => {})
}

/* -------------------- responsive (via MediaQueryList) -------------------- */
let mql: MediaQueryList | null = null
let mqlHandler: ((e: MediaQueryListEvent) => void) | null = null

onMounted(() => {
  loadCategoryTree().catch(() => {})
  mql = window.matchMedia('(max-width: 900px)')
  isMobile.value = !!mql.matches
  mqlHandler = (e) => {
    isMobile.value = e.matches
    if (!e.matches) mobileSidebarOpen.value = false
  }
  mql.addEventListener('change', mqlHandler)
  window.addEventListener(EDITOR_IMAGES_CHANGED_EVENT, onExternalEditorImagesChanged as EventListener)
  refreshProjectImageCounts().catch(() => {})
})

onBeforeUnmount(() => {
  if (mql && mqlHandler) mql.removeEventListener('change', mqlHandler)
  window.removeEventListener(EDITOR_IMAGES_CHANGED_EVENT, onExternalEditorImagesChanged as EventListener)
})

/* -------------------- route params -------------------- */
const { categoryId, productId, categoryIri, productIri } = useRouteQueryIds()

const { result: productData } = useQuery(
  getProduct,
  computed(() => ({ id: productIri.value })),
  { enabled: computed(() => !!productIri.value), fetchPolicy: 'network-only' } as any,
)

// Aktuelle Kategorie laden (für Parent-Info + 3D-Config)
const { result: categoryData } = useQuery(
  getCategory,
  computed(() => ({ id: categoryIri.value })),
  { enabled: computed(() => !!categoryIri.value), fetchPolicy: 'cache-first' } as any,
)

const resolvedPreviewConfig = computed(() => {
  const productCfg = (productData.value as any)?.product?.editorOverrideConfig ?? null
  const categoryCfg = (categoryData.value as any)?.category?.editorConfig ?? null
  return resolvePreview3dOverrides({
    categoryConfig: categoryCfg,
    productConfig: productCfg,
  })
})

// preview3d v2 (Wandbilder): Kategorie → Produkt; Optionen liest die Vorschau aus dem Bild-Datensatz
const preview3dLayers = computed(() => {
  const productCfg = (productData.value as any)?.product?.editorOverrideConfig ?? null
  const categoryCfg = (categoryData.value as any)?.category?.editorConfig ?? null
  const layers = [categoryCfg?.preview3d, productCfg?.preview3d].filter((c: any) => c && Number(c.v) === 2)
  return layers.length ? layers : null
})

// Wenn aktuelle Kategorie eine Unterkategorie ist, liefert parent._id die Root-ID
const parentCategoryId = computed<number | null>(() => {
  const p = (categoryData.value?.category as any)?.parent
  return p ? Number(p._id) : null
})

const activeCategoryId = computed<number | null>(() =>
  parentCategoryId.value ?? categoryId.value
)

const rootCategories = computed(() =>
  (Array.isArray(categoryTree.value?.roots) ? categoryTree.value!.roots : [])
    .filter((c: any) => c?.showInEditor === true)
    .map((c: any) => ({ ...c, productCount: projectRootCategoryCounts.value.get(Number(c._id)) ?? 0 }))
)

/* -------------------- Unterkategorien (horizontaler Strip) -------------------- */
const subcatParentId = computed<number | null>(() => {
  if (parentCategoryId.value) return Number(parentCategoryId.value || 0) || null
  if (categoryId.value) return Number(categoryId.value || 0) || null
  return null
})
const subCategories = computed(() =>
  (Array.isArray(categoryTree.value?.items) ? categoryTree.value!.items : [])
    .filter((c: any) => Number(c?.parentId ?? 0) === Number(subcatParentId.value ?? 0))
    .filter((c: any) => c?.showInEditor === true)
    .map((c: any) => ({ ...c, productCount: projectCategoryCounts.value.get(Number(c._id)) ?? 0 }))
)

function onRootCategorySelect(cat: any) {
  router.push({ query: { ...route.query, category: String(cat._id) } })
}

function onSubCategorySelect(cat: any) {
  router.push({ query: { ...route.query, category: String(cat._id) } })
}

function updateCanvasSize(width: number, height: number) {
  const w = Number(width)
  const h = Number(height)
  if (!Number.isFinite(w) || !Number.isFinite(h) || h <= 0) return
  aspectRatio.value = w / h
}

function setActiveTab(tab: string | null) {
  activeTab.value = tab
}

function goBackToOverview() {
  if (typeof window !== 'undefined' && window.history.length > 1) {
    router.back()
    return
  }
  router.push('/')
}

</script>

<style scoped>
.editor-layout {
  display: grid;
  grid-template-columns: 124px minmax(0, 1fr) 360px;
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
  background: #f4f4f4;
  position: relative;
}

.cat-col {
  grid-column: 1;
  grid-row: 1;
  position: sticky;
  top: 0;
  height: 100vh;
  height: 100dvh;
  z-index: 1;
}

.editor-preview {
  grid-column: 2;
  min-width: 0;
  height: 100vh;
  height: 100dvh;
  background: #dcdcdc;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Toolbar: Zurück-Button */
.preview-toolbar {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  flex-shrink: 0;
  z-index: 20;
  backdrop-filter: blur(4px);
}

.back-overview {
  height: 30px;
  padding: 0 10px;
  border: 1px solid rgba(15, 23, 42, 0.18);
  background: #fff;
  color: #0f172a;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border-radius: 3px;
  white-space: nowrap;
}

/* Unterkategorie-Strip */
.subcat-bar {
  flex-shrink: 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

/* 3D-Preview füllt restliche Höhe */
.preview-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: stretch;
  justify-content: center;
  position: relative;
}

.editor-sidebar {
  grid-column: 3;
  height: 100vh;
  height: 100dvh;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  background: #fff;
  padding: 0;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.08);
}
.editor-sidebar :deep(.sidebar-wrapper){
  height: 100%;
  overflow: auto;
  width: 100%;
  max-width: none;
  padding: 0 12px 12px;
}


.mobile-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.38);
  z-index: 80;
}

.mobile-sidebar {
  position: fixed;
  left: 0;
  right: 0;
  top: calc(env(safe-area-inset-top, 0px) + 72px);
  bottom: 0;
  box-sizing: border-box;
  background: #fff;
  z-index: 90;
  transform: translateY(102%);
  transition: transform .24s ease;
  border-top: 1px solid rgba(15, 23, 42, 0.12);
  box-shadow: 0 -10px 26px rgba(2, 6, 23, 0.18);
  overflow: auto;
}

.mobile-sidebar.open {
  transform: translateY(0);
}

.mobile-sidebar-head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
  background: #fff;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.mobile-close {
  height: 32px;
  padding: 0 10px;
  border: 1px solid rgba(15, 23, 42, 0.18);
  background: #fff;
  font-size: 12px;
  font-weight: 700;
}

@media (max-width: 900px) {
  .editor-layout {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    min-height: 100dvh;
  }

  .cat-col {
    order: -1;
    width: 100%;
    flex-shrink: 0;
  }

  .editor-preview {
    min-height: 0;
    height: auto;
    flex: 1;
  }

  .preview-wrap {
    min-height: 50vh;
    min-height: 50dvh;
  }

  .editor-sidebar {
    width: 100%;
    height: auto;
    box-shadow: none;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
  }
}
</style>
